import { useEffect, useRef, useState } from "react";
import { checkLegality } from "@/lib/api";
import type { LegalityResult, LegalityState } from "@/lib/types";

/**
 * Debounced legality check for a moving pin (spec 06 §4–§5).
 *
 * As the pin's coordinates change, the hook debounces (300 ms) a call to the
 * `check_legality` RPC and derives the four-state UX model:
 *   - green  → allowed, not time-dependent
 *   - amber  → allowed now but time-dependent (pedestrian zone, legal at night)
 *   - red    → forbidden (inside a no-consumption zone)
 *   - grey   → no zone data here (dataCoverage:false)
 *
 * Spec 01 §5 / 06 §4.
 */
export interface UseLegality {
  state: LegalityState;
  result: LegalityResult | null;
  loading: boolean;
  error: Error | null;
}

const DEBOUNCE_MS = 300;

/** Reduce a LegalityResult to the four-state UX model. */
export function deriveState(result: LegalityResult | null): LegalityState {
  if (!result) return "grey";
  if (result.dataCoverage === false) return "grey";
  if (!result.allowed) return "red";
  // Allowed but time-dependent → amber (legal now, forbidden 07–20).
  if (result.timeDependent) return "amber";
  return "green";
}

export function useLegality(
  lng: number | null,
  lat: number | null,
  debounceMs: number = DEBOUNCE_MS,
): UseLegality {
  const [result, setResult] = useState<LegalityResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    // No coordinates yet → idle.
    if (lng === null || lat === null) {
      setResult(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const myId = ++reqId.current;
    const timer = window.setTimeout(async () => {
      try {
        const res = await checkLegality(lng, lat);
        // Ignore stale responses if a newer debounce fired.
        if (myId !== reqId.current) return;
        setResult(res);
        setError(null);
      } catch (e) {
        if (myId !== reqId.current) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        // On error, fall back to grey (no-data) so the UI stays safe.
        setResult(null);
      } finally {
        if (myId === reqId.current) setLoading(false);
      }
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [lng, lat, debounceMs]);

  return { state: deriveState(result), result, loading, error };
}
